/* Static marketing copy + fallback content for Forte by Ryann. */

export const NAV_LINKS = [
  { id: "digital-studio", label: "Digital Studio", href: "/digital-studio" },
  { id: "method", label: "Method", href: "/#method" },
  { id: "privates", label: "Privates", href: "/#privates" },
  { id: "events", label: "Events", href: "/#events" },
  { id: "testimonials", label: "Voices", href: "/#testimonials" },
  { id: "contact", label: "Contact", href: "/#contact" },
];

export const HERO = {
  eyebrow: "Athletic Pilates \u00b7 Strength \u00b7 Mobility",
  headline_top: "Move with",
  headline_accent: "intention.",
  headline_bottom: "Build real strength.",
  sub: "A method that blends mobility, athletic Pilates and progressive strength training into one intentional practice \u2014 designed to deliver real, measurable results.",
};

export const MARQUEE_WORDS = [
  "Mobility",
  "Athletic Pilates",
  "Strength",
  "Endurance",
  "Stability",
  "Agility",
  "Balance",
  "Body Awareness",
  "Progression",
];

export const DIFFERENTIATOR = {
  headline: "No repeated workouts. No random workouts.",
  body: "Every class is intentionally programmed as part of a bigger progression \u2014 so you're continuously building strength, mobility, endurance, and athleticism week after week.",
};

export const METHOD = {
  eyebrow: "The Forte Method",
  headline_lead: "More Than Pilates.",
  headline_accent: "More Than Strength.",
  body: [
    "With Forte by Ryann, you'll find a method that blends mobility, athletic Pilates and strength training into one intentional, progressive practice designed to deliver real and measurable results.",
    "Unlike traditional fitness classes that recycle the same workouts week after week, no two sessions here are ever the same. Every class is thoughtfully programmed with purpose \u2014 building on the progress we've made together while introducing new challenges, movement patterns and opportunities for growth.",
  ],
  closer: "Rooted in control. Built for strength.",
  goal: "The goal is simple: continual, intelligent progression.",
  pillars: [
    {
      num: "01",
      title: "Classical Foundation",
      body: "Control, alignment, and intelligent sequencing.",
    },
    {
      num: "02",
      title: "Modern Intensity",
      body: "Athletic, dynamic, and shaped with purpose.",
    },
    {
      num: "03",
      title: "Sculpt-Driven Movement",
      body: "Designed to lengthen, strengthen, and refine.",
    },
    {
      num: "04",
      title: "Intentional Instruction",
      body: "Clear cues and personalized attention in motion.",
    },
    {
      num: "05",
      title: "Confidence Transformation",
      body: "A stronger body, a steadier mind, a deeper presence.",
    },
  ],
};

export const FOUNDATION = {
  eyebrow: "The Foundation",
  headline_lead: "Mobility",
  headline_accent: "& Activation.",
  intro:
    "Every class begins the same way: with a 10\u201315 minute lymphatic and mobility activation sequence.",
  body: [
    "This intentional warm-up is far more than a stretch. It's the foundation of the entire method and one of the key reasons clients experience such significant improvements in the way they move, perform and feel.",
    "It's designed to increase circulation, stimulate the lymphatic system, improve joint mobility, activate key stabilizing muscles and prepare the body for movement \u2014 creating space in the joints, improving range of motion and establishing the muscular engagement needed to move with strength, control, and confidence.",
    "The goal isn't simply to become more flexible \u2014 it's to become more mobile, resilient, and capable. By intentionally activating the core, glutes, shoulders, and feet before we begin, we move with greater precision and get more out of every exercise that follows.",
  ],
};

export const STUDIO = {
  eyebrow: "The Forte Digital Studio",
  headline_lead: "Your complete studio.",
  headline_accent: "$45 a month.",
  body: "Train live with Ryann from anywhere through six online classes each week, then keep moving with the membership-only on-demand library and published class replays. It is the same intentional Forte progression, built for a digital-first routine.",
  scheduleTitle: "Six Live Classes Every Week",
  schedule: [
    { days: "Monday & Wednesday", times: "Strength" },
    { days: "Tuesday & Thursday", times: "Sculpt" },
    { days: "Friday", times: "Upper Body / Arms / Abs" },
    { days: "Sunday", times: "High-Intensity Pilates" },
  ],
  access: "Live classes, member-enabled on-demand workouts, and published replays are included while your membership is active.",
};

export const EVENTS = {
  eyebrow: "Events & Partnerships",
  headline_lead: "Wellness, shaped for",
  headline_accent: "memorable spaces.",
  body: "Forte creates polished movement experiences for brands, teams, retreats, hotels, resorts, boutiques, and private groups who want Pilates to feel considered from first welcome to final breath.",
  categories: [
    "Corporate Wellness",
    "Retreat Programming",
    "Hotel & Resort Activations",
    "Boutique Partnerships",
    "Private Group Events",
    "Specialty Bookings",
  ],
};

export const PRIVATES = {
  eyebrow: "Private Training",
  headline_lead: "Programming made",
  headline_accent: "entirely for you.",
  body: "One-on-one and dedicated small-group training built around your goals, your body, and your pace \u2014 with personalized nutrition and lifestyle guidance that supports your progress far beyond the studio.",
  points: [
    "Goal-based, fully personalized programming",
    "Attentive form correction and modifications",
    "Nutrition & lifestyle guidance",
    "Progress that compounds week after week",
  ],
};

export const TESTIMONIALS_INTRO = {
  eyebrow: "Client Voices",
  headline_lead: "Felt in the body.",
  headline_accent: "Carried into the day.",
  body: "What clients say after training inside the Forte method \u2014 stronger, steadier, and more connected to what their bodies can do.",
};

export const CONTACT = {
  eyebrow: "Contact",
  headline_lead: "Let's begin",
  headline_accent: "your progression.",
  body: "Questions about the Digital Studio, private training, or partnerships? Send a note and we'll be in touch.",
  interests: [
    "Digital Studio",
    "Private Training",
    "Small-Group Training",
    "Events & Partnerships",
    "Something else",
  ],
};

/* ---- Homepage class-format details ---- */
export const FALLBACK_CLASS_FORMATS = [
  {
    id: "strength",
    name: "Strength",
    days: "Monday \u00b7 Wednesday",
    tagline: "Our muscle-building days.",
    intro: "Full-body athletic Pilates and progressive strength training, coached live online.",
    description:
      "A dynamic blend of athletic Pilates and progressive strength training. Expect intentional, full-body work that builds week over week. It is the foundation of the Forte method.",
    equipment: ["Mat", "Light and heavy weights"],
    focus:
      "Strength, muscle development, power, athletic performance, and progressive overload.",
    image: "/media/class-strength.jpg",
  },
  {
    id: "sculpt",
    name: "Sculpt",
    days: "Tuesday \u00b7 Thursday",
    tagline: "Refine, sculpt, and strengthen.",
    intro: "Precision Pilates with a focused burn that complements the week's Strength work.",
    description:
      "Designed to refine, sculpt, and strengthen the muscle we build on Strength days. Precision Pilates, layered movement patterns, and time under tension create a burn you'll feel tomorrow.",
    equipment: ["Mat", "Light weights", "Resistance band", "Pilates ball"],
    focus:
      "Muscle definition, endurance strength, stability, coordination, and Pilates mastery.",
    image: "/media/class-sculpt.jpg",
  },
  {
    id: "upper-body-arms-abs",
    name: "Upper Body / Arms / Abs",
    days: "Friday",
    tagline: "Finish the week strong.",
    intro: "Focused upper-body strength paired with deep abdominal training.",
    description:
      "Arms, shoulders, back, and core come together in a focused upper-body burn that pairs intentional strength work with deep abdominal training. Walk into the weekend feeling strong and tall.",
    equipment: ["Mat", "Light and heavy weights"],
    focus:
      "Upper-body strength, posture, shoulder stability, core control, and muscular endurance.",
    image: "/media/privates.jpg",
  },
  {
    id: "high-intensity-pilates",
    name: "High-Intensity Pilates",
    days: "Sunday",
    tagline: "Think of this as my version of running.",
    intro: "A sweaty, athletic Pilates reset to launch your week.",
    description:
      "The precision and control of Pilates meet the intensity of high-energy interval training. This fast-paced, athletic session elevates your heart rate and challenges endurance while staying grounded in intentional movement.",
    equipment: ["Mat", "Ankle weights (optional)"],
    focus:
      "Cardiovascular fitness, endurance, stamina, athletic conditioning and full-body sculpting.",
    image: "/media/class-hiit.jpg",
  },
];

export const FALLBACK_TESTIMONIALS = [
  { id: "chloe-c", name: "Chloe C", label: "Group Class Client", featured: true, quote: "Forte by Ryann is an amazing experience and training session. She creates a supportive, motivating environment while still pushing you to get stronger. She’s attentive, encouraging, and incredibly knowledgeable, making every session both challenging and enjoyable. I always leave feeling stronger and more aligned." },
  { id: "julianne-g", name: "Julianne G", label: "Group Class Client", featured: false, quote: "Ryann is by far one of the absolute best Pilates instructors I’ve ever experienced! Her dynamic sets and calm, but fierce encouragement provide the perfect environment to get a great physical workout, with an enhanced mental workout as well. Thank you Ryann!" },
  { id: "harley-j", name: "Harley J", label: "Group Class Client", featured: false, quote: "Class with Ryann is a wonderful experience—and always challenging during! She is a fantastic instructor who truly cares about her clients and is incredibly attentive to proper form. It’s a great combination that has really helped me progress in my Pilates journey." },
  { id: "kate-m", name: "Kate M", label: "Group Class Client", featured: true, quote: "Ryann is an absolutely incredible Pilates instructor! I genuinely look forward to her classes every week—not just for the workout, but for her bubbly, positive energy. Since starting Ryann’s classes, I’ve noticed a huge improvement in my strength and muscle endurance, and it even carries over into other workouts I do. She’s also incredibly generous with her knowledge, sharing helpful insights on both workouts and nutrition. I can’t recommend her enough!" },
  { id: "nickole-m", name: "Nickole M", label: "Group Class Client", featured: false, quote: "Ryann is truly one of a kind. She is one of the kindest, most positive people I know, but don’t let that fool you—she will also push you, challenge you, and absolutely kick your butt in the best way possible. I’m the healthiest I’ve ever been, not only because of Ryann’s Pilates classes, but because of her holistic approach to wellness. Her energy is inspiring, her knowledge is empowering, and her encouragement makes every workout feel purposeful." },
  { id: "isabella-p", name: "Isabella P", label: "Small-Group Client", featured: false, quote: "Ever since I met you, I’ve been completely obsessed with your workouts. You’ve pushed me in the best way and genuinely helped me reset my whole health and wellness routine. Your workouts have truly transformed my body in ways I didn’t think were possible. I tell all my friends about you because I genuinely don’t see myself ever going to another Pilates studio." },
  { id: "dakota-b", name: "Dakota B", label: "Private & Small-Group Client", featured: true, quote: "Ryann is an amazing coach. Not only does she make everyone feel strong, she always provides modifications in every class so you still feel confident, capable, and successful with every movement. She has a way of making you feel like you’re her only client, even in a group setting. She truly goes above and beyond. She is the best, and I don’t see a time in the future where we won’t be working out with her!" },
  { id: "nicole-s", name: "Nicole S", label: "Group Class Client", featured: false, quote: "Never have I felt so motivated in a workout class before! Ryann has an amazing way of curating challenging classes that produce real results. I’m always in a better mood when I leave her classes." },
  { id: "lana-f", name: "Lana F", label: "Group Class Client", featured: false, quote: "Ryann is the definition of a dedicated, experienced Pilates instructor and she truly brings fitness to the next level. Her classes are challenging, well-structured (never boring!) and incredibly effective—deeply focused on client progress and building real strength. I feel stronger than I ever have since incorporating Ryann’s classes into my routine." },
  { id: "danella-p", name: "Danella P", label: "Park & Group Client", featured: false, quote: "I’ve absolutely loved training with Ryann! I had never done Pilates before and started with her outdoor park classes—and loved them so much that I now go weekly. Her workouts are challenging in the best way; she pushes you past your comfort zone while still making you feel supported and capable. If you’re new to Pilates or looking to level up, she’s the best!" },
  { id: "larissa-s", name: "Larissa S", label: "Private Client", featured: false, quote: "Ryann lights up a room with her energy before quickly getting down to business offering a technique-focused burn. If you spend the time to share your goals or restrictions with Ryann, she will help provide a challenging yet safe workout for your condition, like she did for me while I needed modifications during pregnancy." },
  { id: "sami-s", name: "Sami S", label: "Private Client", featured: true, quote: "I cannot recommend training with Ryann enough—she is the best fitness coach I have ever had and an amazing human. I don’t just look at her as a fitness coach, but as a health coach who is there to help me achieve my goals and live my best, healthiest life. After working with Ryann I am the most confident I have ever been in my body, I have more energy, and my body not only looks better, it feels better." },
  { id: "chris-bonner", name: "Chris Bonner", label: "Group Class Client", featured: false, quote: "I’ve been training with Ryann now for a few months and it has been the best fitness experience of my adult life. She consistently finds ways to make each session unique and challenging, and she is an incredible motivator. I have already noticed increased mobility, core strength, and my lower back pain is gone. Take a session with her and you will understand!" },
  { id: "chloe-o", name: "Chloe O", label: "Group Class Client", featured: false, quote: "I feel so fortunate to be able to train with Ryann! She’s an excellent instructor that challenges me while simultaneously supporting me, and helps me feel continuously stronger and more confident in my abilities. I really appreciate her knowledge sharing about overall health and wellness that really rounds out a great workout." },
  { id: "kat-f", name: "Kat F", label: "Group Class Client", featured: false, quote: "Ryann has not only transformed my body’s strength and increased my range of mobility, but she has helped me build inner confidence. ForteByRyann has allowed me to step into my confidence and feel strong in my body. Ryann has created an environment free of judgment where people can come to build community, grow stronger together and discover what their bodies are truly capable of." },
  { id: "sam-d", name: "Sam D", label: "Group Class Client", featured: false, quote: "Ryann is the BEST Pilates instructor with the most innovative and effective workouts. If you’re looking for the best burn and high yield results, she’s your girl." },
  { id: "ana-o", name: "Ana O", label: "Group Class Client", featured: false, quote: "Ryann is a kind, welcoming, caring and experienced teacher. She does a great job of creating a communal experience during a group class with her warm and supportive energy. Whether you are new or experienced you will always try and learn something new with Ryann. She’s amazing!" },
  { id: "alex-r", name: "Alex R", label: "Group Class Client", featured: false, quote: "Ryann meticulously plans class each day and the prep shows! The sessions are always challenging in the best way. She is mindful of always offering modifications which is very helpful for me because I have bad shoulders. I would recommend her classes to anybody at any fitness level who is looking to step up their game!" },
];
